import React from 'react';
import { Box, Typography, Chip, Stack, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useSelector } from 'react-redux';

/**
 * Component to display the keys from the arrayKeys map.
 * @param {Object} props - The component props.
 * @param {Map<string, boolean>} props.arrayKeys - The map containing keys that correspond to array values in the filter.
 * @param {Object} props.filter - The filter object.
 * @returns {JSX.Element}
 */
const ArrayKeysDisplay = ({ arrayKeys, filter }) => {

  const {
    Designations,
    Departments,
    Languages,
    Districts,
    Priviledges,
    Shifts,
    TaskTypes,
    Campaigns,
    Hierarchies,
    FieldTypes,
    Validations,
    CampaignForms,
    LeaveType,
    Query,
    SubQuery,
    UnassignReasons,
    users
  } = useSelector(state => state.user || {});

  const keys = Array.from(arrayKeys.keys());

  if (keys.length === 0) {
    return null; // Don't render anything if there are no array keys
  }

  // Helper function to render chips for a given key
  const renderChips = (key) => {
    const values = filter[key];

    if (!values || !Array.isArray(values) || values.length === 0) {
      // Handle cases where filter[key] is not a non-empty array
      return <Chip label="No items selected" size="small" />;
    }

    switch (key) {
      case 'district': {
        if (!Districts) return <Chip label="District data unavailable" size="small" />;
        const districtNames = values
          .map(id => {
            const district = Districts.find(d => d._id === id);
            return district ? district.name : null;
          })
          .filter(name => name !== null);

        if (districtNames.length === 0) {
          return <Typography variant="caption">No matching districts found</Typography>;
        }
        return districtNames.map(name => (
          <Chip key={`${key}-${name}`} label={name} size="small" />
        ));
      }
      case 'allocatedToUser': {
        if (!users) return <Chip label="User data unavailable" size="small" />;
        const userNames = values
          .map(id => {
            const user = users.find(u => u._id === id);
            // Assuming user object has a 'name' or 'username' field
            return user ? (user.name || user.username) : null;
          })
          .filter(name => name !== null);

        if (userNames.length === 0) {
          return <Typography variant="caption">No matching users found</Typography>;
        }
        return userNames.map(name => (
          <Chip key={`${key}-${name}`} label={name} size="small" />
        ));
      }
      default:
        // Default case for other array keys
        return <Chip label={`${values.length} item(s) selected`} size="small" />;
    }
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom sx={{ mb: 1 }}>
        Array Filters:
      </Typography>
      {keys.map((key) => (
        <Accordion key={key} sx={{ '&.Mui-expanded:first-of-type': { marginTop: 0 }, '&:before': { display: 'none' }, border: '1px solid rgba(0, 0, 0, .125)', boxShadow: 'none' }}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls={`${key}-content`}
            id={`${key}-header`}
            sx={{ minHeight: '40px', '& .MuiAccordionSummary-content': { marginY: '8px' } }}
          >
            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{key.replace(/([A-Z])/g, ' $1')}</Typography> {/* Add space before caps */}
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0, pb: 1, px: 2 }}>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {renderChips(key)}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default ArrayKeysDisplay;
